import React, { useState, useRef, useEffect } from "react";
import { G, SPACING, TYPE, FONT_FAMILY, OVERVIEW } from "../../constants/theme";
import { useAuth } from "../../contexts/AuthContext";
import { useTour } from "../../tour/TourContext";
import ecosenseLogo from "../../assets/ecosense-logo.png";

export default function TopNav({
  syncing = false,
  onSync,
  syncStatus = null,
  searchQuery = "",
  onSearchChange,
  searchResults = [],
  sprint = "",
  sprints = [],
  onSprintChange,
}) {
  const { user, logout } = useAuth();
  const { start: startTour } = useTour();
  const [sprintOpen, setSprintOpen] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchRef = useRef(null);
  const sprintRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    function onDown(e) {
      if (sprintRef.current && !sprintRef.current.contains(e.target)) setSprintOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchActive(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const showSearchDrop = searchActive && searchResults.length > 0;

  return (
    <nav
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        height: SPACING.navH,
        background: G.dark,
        borderBottom: `1px solid ${G.darkBorder}`,
        fontFamily: FONT_FAMILY,
      }}
    >
     <div
      style={{
        height: "100%",
        maxWidth: OVERVIEW.contentMax,
        margin: "0 auto",
        padding: `0 ${OVERVIEW.gutter}px`,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 0,
      }}
     >
      {/* Brand mark: same cropped EcoSense icon as the sidebar, contained
          in a white badge so it reads correctly against the dark header. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 28, flexShrink: 0 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            background: G.white,
            overflow: "hidden",
            position: "relative",
            flexShrink: 0,
          }}
        >
          <img
            src={ecosenseLogo}
            alt="EcoSense"
            style={{
              position: "absolute",
              left: -9,
              top: -1,
              width: 74,
              height: "auto",
              maxWidth: "none",
            }}
          />
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, color: G.white, whiteSpace: "nowrap" }}>
          JiraOps
        </span>
      </div>

      {/* Search */}
      <div ref={searchRef} style={{ position: "relative", flex: 1, maxWidth: 260 }}>
        <div style={{ position: "relative" }}>
          <span
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "rgba(255,255,255,0.28)",
              fontSize: 14,
              pointerEvents: "none",
              lineHeight: 1,
            }}
          >
            ⌕
          </span>
          <input
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onFocus={() => setSearchActive(true)}
            placeholder="Search tickets…"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "5px 10px 5px 30px",
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${searchActive ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 4,
              color: "rgba(255,255,255,0.8)",
              fontSize: 12,
              fontFamily: FONT_FAMILY,
              outline: "none",
              transition: "border-color 0.15s",
            }}
          />
        </div>

        {showSearchDrop && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              background: G.darkSlate,
              border: `1px solid ${G.darkBorder}`,
              borderRadius: 4,
              overflow: "hidden",
              zIndex: 200,
              boxShadow: "0 8px 24px rgba(7,26,47,0.45)",
            }}
          >
            {searchResults.map((r, i) => (
              <div
                key={i}
                style={{
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  borderBottom:
                    i < searchResults.length - 1
                      ? `1px solid ${G.darkBorder}`
                      : "none",
                  cursor: "default",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.04)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <span
                  style={{
                    fontFamily: TYPE.mono.fontFamily,
                    fontSize: 11,
                    color: G.brand,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.ticketNo}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    background: "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.5)",
                    padding: "1px 5px",
                    borderRadius: 2,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {r.type}
                </span>
                {r.assignedTo && (
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginLeft: "auto" }}>
                    {r.assignedTo}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right: Sprint + Sync + Avatar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 16, flexShrink: 0 }}>
        {/* Sprint selector */}
        {sprint && (
          <div ref={sprintRef} style={{ position: "relative" }}>
            <button
              onClick={() => setSprintOpen((o) => !o)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                background: "transparent",
                border: `1px solid rgba(255,255,255,0.1)`,
                borderRadius: 4,
                color: "rgba(255,255,255,0.55)",
                fontSize: 12,
                fontWeight: 400,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "border-color 0.15s, color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.22)";
                e.currentTarget.style.color = "rgba(255,255,255,0.8)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                e.currentTarget.style.color = "rgba(255,255,255,0.55)";
              }}
            >
              {sprint}
              <span style={{ fontSize: 9, opacity: 0.5, marginLeft: 2 }}>▾</span>
            </button>

            {sprintOpen && sprints.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  right: 0,
                  background: G.darkSlate,
                  border: `1px solid ${G.darkBorder}`,
                  borderRadius: 4,
                  overflow: "hidden",
                  zIndex: 200,
                  minWidth: 200,
                  boxShadow: "0 8px 24px rgba(7,26,47,0.45)",
                }}
              >
                {sprints.map((s) => (
                  <div
                    key={s}
                    onClick={() => {
                      onSprintChange?.(s);
                      setSprintOpen(false);
                    }}
                    style={{
                      padding: "8px 14px",
                      fontSize: 12,
                      color: s === sprint ? G.white : "rgba(255,255,255,0.55)",
                      fontWeight: s === sprint ? 600 : 400,
                      cursor: "pointer",
                      borderBottom: `1px solid ${G.darkBorder}`,
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(255,255,255,0.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sync status feedback */}
        {syncStatus && (
          <span
            style={{
              fontSize: 11,
              color: syncStatus === "error" ? G.red600 : G.green500,
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {syncStatus === "error" ? "Sync failed" : "Synced"}
          </span>
        )}

        {/* User Guide — starts the guided walkthrough */}
        <button
          type="button"
          onClick={() => startTour("dashboard")}
          title="Take a guided tour of the dashboard"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 12px",
            borderRadius: 4,
            border: "1px solid rgba(147,197,253,0.35)",
            background: "rgba(37,99,235,0.12)",
            color: "#93C5FD",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: FONT_FAMILY,
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "background 0.15s, border-color 0.15s, color 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(37,99,235,0.22)";
            e.currentTarget.style.borderColor = "rgba(147,197,253,0.6)";
            e.currentTarget.style.color = "#FFFFFF";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(37,99,235,0.12)";
            e.currentTarget.style.borderColor = "rgba(147,197,253,0.35)";
            e.currentTarget.style.color = "#93C5FD";
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 14,
              height: 14,
              borderRadius: "50%",
              border: "1.5px solid currentColor",
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            ?
          </span>
          User Guide
        </button>

        {/* Sync button */}
        {onSync && (
          <button
            onClick={onSync}
            disabled={syncing}
            style={{
              padding: "5px 12px",
              borderRadius: 4,
              border: `1px solid rgba(255,255,255,0.1)`,
              background: "transparent",
              color: syncing ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.6)",
              fontSize: 12,
              fontWeight: 500,
              fontFamily: FONT_FAMILY,
              cursor: syncing ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              if (!syncing) {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.22)";
                e.currentTarget.style.color = "rgba(255,255,255,0.85)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
              e.currentTarget.style.color = syncing
                ? "rgba(255,255,255,0.25)"
                : "rgba(255,255,255,0.6)";
            }}
          >
            {syncing ? "Syncing…" : "Sync Jira"}
          </button>
        )}

        {/* User avatar + menu */}
        <div ref={userMenuRef} style={{ position: "relative" }}>
          <div
            onClick={() => setUserMenuOpen((o) => !o)}
            title={user ? `${user.displayName} — click to sign out` : ""}
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: userMenuOpen ? G.brand : G.darkSubtle,
              border: `1px solid ${userMenuOpen ? G.brand : "rgba(255,255,255,0.12)"}`,
              color: "rgba(255,255,255,0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.02em",
              flexShrink: 0,
              cursor: "pointer",
              transition: "background 0.15s, border-color 0.15s",
              userSelect: "none",
            }}
          >
            {user
              ? user.displayName.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
              : "?"}
          </div>

          {userMenuOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                background: G.darkSlate,
                border: `1px solid ${G.darkBorder}`,
                borderRadius: 6,
                minWidth: 200,
                boxShadow: "0 8px 24px rgba(7,26,47,0.5)",
                zIndex: 300,
                overflow: "hidden",
              }}
            >
              {/* User info */}
              <div style={{ padding: "12px 14px", borderBottom: `1px solid ${G.darkBorder}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: G.white, fontFamily: FONT_FAMILY }}>
                  {user?.displayName}
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontFamily: FONT_FAMILY, marginTop: 2 }}>
                  {user?.email}
                </div>
                <div
                  style={{
                    display: "inline-block",
                    marginTop: 6,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    padding: "2px 7px",
                    borderRadius: 3,
                    background: "rgba(37,99,235,0.18)",
                    color: "#93C5FD",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {user?.role ?? "DEVELOPER"}
                </div>
              </div>

              {/* Sign out */}
              <button
                onClick={() => { setUserMenuOpen(false); logout(); }}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "10px 14px",
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  color: "rgba(255,255,255,0.6)",
                  fontSize: 13,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                  transition: "background 0.1s, color 0.1s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(220,38,38,0.12)";
                  e.currentTarget.style.color = "#FCA5A5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "none";
                  e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
     </div>
    </nav>
  );
}
